import React, { useState } from 'react';
import { Scan, Download, FileImage, Sliders, Eye, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import { Button, Card, FileUploadZone, ProgressBar, Alert, Badge, PDFPreview } from '../shared';
import { PDFDocument } from 'pdf-lib';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import Tesseract from 'tesseract.js';
import '../../styles/design-system.css';

const ScanToPDF = () => {
    const [images, setImages] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [pageSize, setPageSize] = useState('a4');
    const [orientation, setOrientation] = useState('portrait');
    const [applyOCR, setApplyOCR] = useState(false);
    const [ocrLanguage, setOCRLanguage] = useState('eng');
    const [filter, setFilter] = useState('none');
    const [previewBlob, setPreviewBlob] = useState(null);
    const [previewFilename, setPreviewFilename] = useState('');

    const handleFilesSelected = (selectedFiles) => {
        const newImages = selectedFiles.map((file, index) => ({
            id: Date.now() + index,
            file: file,
            name: file.name,
            size: file.size,
            preview: URL.createObjectURL(file),
            processedPreview: null,
            ocrText: null
        }));
        setImages(prev => [...prev, ...newImages]);
        toast.success(`${selectedFiles.length} image(s) added`);
    };

    const removeImage = (id) => {
        setImages(prev => {
            const image = prev.find(img => img.id === id);
            if (image) {
                URL.revokeObjectURL(image.preview);
                if (image.processedPreview) {
                    URL.revokeObjectURL(image.processedPreview);
                }
            }
            return prev.filter(img => img.id !== id);
        });
        toast.info('Image removed');
    };

    const moveImage = (index, direction) => {
        const newImages = [...images];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;

        if (targetIndex < 0 || targetIndex >= images.length) return;

        [newImages[index], newImages[targetIndex]] = [newImages[targetIndex], newImages[index]];
        setImages(newImages);
    };

    // Apply image filters
    const applyImageFilter = async (imageUrl, filterType) => {
        return new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                canvas.width = img.width;
                canvas.height = img.height;

                ctx.drawImage(img, 0, 0);

                if (filterType !== 'none') {
                    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                    const data = imageData.data;

                    switch (filterType) {
                        case 'grayscale':
                            for (let i = 0; i < data.length; i += 4) {
                                const avg = (data[i] + data[i + 1] + data[i + 2]) / 3;
                                data[i] = data[i + 1] = data[i + 2] = avg;
                            }
                            break;
                        case 'brightness':
                            for (let i = 0; i < data.length; i += 4) {
                                data[i] = Math.min(255, data[i] + 30);
                                data[i + 1] = Math.min(255, data[i + 1] + 30);
                                data[i + 2] = Math.min(255, data[i + 2] + 30);
                            }
                            break;
                        case 'contrast':
                            const factor = 1.3;
                            for (let i = 0; i < data.length; i += 4) {
                                data[i] = Math.min(255, Math.max(0, factor * (data[i] - 128) + 128));
                                data[i + 1] = Math.min(255, Math.max(0, factor * (data[i + 1] - 128) + 128));
                                data[i + 2] = Math.min(255, Math.max(0, factor * (data[i + 2] - 128) + 128));
                            }
                            break;
                        case 'sharpen':
                            // Simple edge detection for sharpening
                            const kernel = [-1, -1, -1, -1, 9, -1, -1, -1, -1];
                            const side = Math.round(Math.sqrt(kernel.length));
                            const halfSide = Math.floor(side / 2);
                            const w = canvas.width;
                            const h = canvas.height;
                            const output = ctx.createImageData(w, h);

                            for (let y = 0; y < h; y++) {
                                for (let x = 0; x < w; x++) {
                                    let r = 0, g = 0, b = 0;
                                    for (let cy = 0; cy < side; cy++) {
                                        for (let cx = 0; cx < side; cx++) {
                                            const scy = y + cy - halfSide;
                                            const scx = x + cx - halfSide;
                                            if (scy >= 0 && scy < h && scx >= 0 && scx < w) {
                                                const offset = (scy * w + scx) * 4;
                                                const wt = kernel[cy * side + cx];
                                                r += data[offset] * wt;
                                                g += data[offset + 1] * wt;
                                                b += data[offset + 2] * wt;
                                            }
                                        }
                                    }
                                    const dstOff = (y * w + x) * 4;
                                    output.data[dstOff] = Math.min(255, Math.max(0, r));
                                    output.data[dstOff + 1] = Math.min(255, Math.max(0, g));
                                    output.data[dstOff + 2] = Math.min(255, Math.max(0, b));
                                    output.data[dstOff + 3] = data[dstOff + 3];
                                }
                            }
                            ctx.putImageData(output, 0, 0);
                            resolve(canvas.toDataURL('image/jpeg', 0.95));
                            return;
                    }

                    ctx.putImageData(imageData, 0, 0);
                }

                resolve(canvas.toDataURL('image/jpeg', 0.95));
            };
            img.src = imageUrl;
        });
    };

    // Perform OCR on image
    const performOCR = async (imageUrl, language) => {
        try {
            const result = await Tesseract.recognize(imageUrl, language, {
                logger: (m) => {
                    if (m.status === 'recognizing text') {
                        // Update progress if needed
                    }
                }
            });
            return result.data.text;
        } catch (error) {
            console.error('OCR error:', error);
            return null;
        }
    };

    const convertToPDF = async () => {
        if (images.length === 0) {
            toast.warning('Please add at least one image');
            return;
        }

        try {
            setIsProcessing(true);
            setProgress(0);

            const pdfDoc = await PDFDocument.create();
            setProgress(5);

            // Page size dimensions
            const pageSizes = {
                a4: orientation === 'portrait' ? [595, 842] : [842, 595],
                letter: orientation === 'portrait' ? [612, 792] : [792, 612],
                legal: orientation === 'portrait' ? [612, 1008] : [1008, 612]
            };

            const [pageWidth, pageHeight] = pageSizes[pageSize];

            for (let i = 0; i < images.length; i++) {
                const image = images[i];
                const baseProgress = 5 + ((i / images.length) * 85);
                setProgress(baseProgress);

                // Apply filter if selected
                let imageUrl = image.preview;
                if (filter !== 'none') {
                    imageUrl = await applyImageFilter(image.preview, filter);
                    setProgress(baseProgress + 10);
                }

                // Perform OCR if enabled
                if (applyOCR) {
                    toast.info(`Processing OCR for image ${i + 1}...`);
                    const ocrText = await performOCR(imageUrl, ocrLanguage);
                    if (ocrText) {
                        images[i].ocrText = ocrText;
                    }
                    setProgress(baseProgress + 20);
                }

                // Convert image to bytes
                const response = await fetch(imageUrl);
                const imageBytes = await response.arrayBuffer();

                // Embed image
                let embeddedImage;
                if (image.file.type === 'image/jpeg' || image.file.type === 'image/jpg') {
                    embeddedImage = await pdfDoc.embedJpg(imageBytes);
                } else if (image.file.type === 'image/png') {
                    embeddedImage = await pdfDoc.embedPng(imageBytes);
                } else {
                    toast.error(`Unsupported image format: ${image.name}`);
                    continue;
                }

                // Create page
                const page = pdfDoc.addPage([pageWidth, pageHeight]);

                // Calculate dimensions
                const imageAspectRatio = embeddedImage.width / embeddedImage.height;
                const pageAspectRatio = pageWidth / pageHeight;

                let drawWidth, drawHeight;
                if (imageAspectRatio > pageAspectRatio) {
                    drawWidth = pageWidth - 40;
                    drawHeight = drawWidth / imageAspectRatio;
                } else {
                    drawHeight = pageHeight - 40;
                    drawWidth = drawHeight * imageAspectRatio;
                }

                const x = (pageWidth - drawWidth) / 2;
                const y = (pageHeight - drawHeight) / 2;

                page.drawImage(embeddedImage, { x, y, width: drawWidth, height: drawHeight });

                // Add OCR text as invisible layer if available
                if (applyOCR && images[i].ocrText) {
                    // Note: pdf-lib doesn't support invisible text layers directly
                    // OCR text is stored but not embedded in this implementation
                    // For production, consider using a library that supports text layers
                }

                setProgress(baseProgress + 30);
            }

            setProgress(95);

            const pdfBytes = await pdfDoc.save();
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });

            setProgress(100);

            const timestamp = new Date().toISOString().slice(0, 10);
            const filename = `scanned-${timestamp}.pdf`;

            // Set preview
            setPreviewBlob(blob);
            setPreviewFilename(filename);

            toast.success('Images converted! Opening preview...');

            // Show OCR results if available
            if (applyOCR) {
                const ocrResults = images.filter(img => img.ocrText).map((img, i) =>
                    `Page ${i + 1}: ${img.ocrText.substring(0, 100)}...`
                ).join('\n\n');

                if (ocrResults) {
                    console.log('OCR Results:', ocrResults);
                    toast.info('OCR completed! Check console for extracted text.');
                }
            }

            setTimeout(() => {
                setProgress(0);
            }, 1000);

        } catch (error) {
            console.error('Conversion error:', error);
            toast.error(`Failed to convert images: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    return (
        <div>
            {/* Header */}
            <div className="tool-header">
                <h1 className="tool-title">
                    <Scan size={36} style={{ display: 'inline', marginRight: 'var(--spacing-3)', color: 'var(--color-accent)' }} />
                    Scan to PDF
                </h1>
                <p className="tool-description">
                    Convert scanned images to PDF with OCR recognition. Apply filters, enhance quality, and extract text from images.
                </p>
            </div>

            {/* File Upload */}
            <FileUploadZone
                onFilesSelected={handleFilesSelected}
                acceptedTypes={['image/jpeg', 'image/jpg', 'image/png']}
                multiple={true}
                title="Drop scanned images here"
                description="or click to browse (JPG, PNG supported)"
            />

            {/* Settings */}
            {images.length > 0 && (
                <>
                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Card>
                            <h3 style={{
                                fontSize: 'var(--text-xl)',
                                fontWeight: 'var(--font-bold)',
                                marginBottom: 'var(--spacing-4)'
                            }}>
                                <Sliders size={20} style={{ display: 'inline', marginRight: 'var(--spacing-2)' }} />
                                Scan Settings
                            </h3>

                            <div className="grid-2" style={{ marginBottom: 'var(--spacing-4)' }}>
                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontSize: 'var(--text-sm)',
                                        fontWeight: 'var(--font-medium)',
                                        marginBottom: 'var(--spacing-2)',
                                        color: 'var(--color-text-secondary)'
                                    }}>
                                        Page Size
                                    </label>
                                    <select
                                        value={pageSize}
                                        onChange={(e) => setPageSize(e.target.value)}
                                        className="input"
                                    >
                                        <option value="a4">A4 (210 × 297 mm)</option>
                                        <option value="letter">Letter (8.5 × 11 in)</option>
                                        <option value="legal">Legal (8.5 × 14 in)</option>
                                    </select>
                                </div>

                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontSize: 'var(--text-sm)',
                                        fontWeight: 'var(--font-medium)',
                                        marginBottom: 'var(--spacing-2)',
                                        color: 'var(--color-text-secondary)'
                                    }}>
                                        Orientation
                                    </label>
                                    <select
                                        value={orientation}
                                        onChange={(e) => setOrientation(e.target.value)}
                                        className="input"
                                    >
                                        <option value="portrait">Portrait</option>
                                        <option value="landscape">Landscape</option>
                                    </select>
                                </div>

                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontSize: 'var(--text-sm)',
                                        fontWeight: 'var(--font-medium)',
                                        marginBottom: 'var(--spacing-2)',
                                        color: 'var(--color-text-secondary)'
                                    }}>
                                        Image Filter
                                    </label>
                                    <select
                                        value={filter}
                                        onChange={(e) => setFilter(e.target.value)}
                                        className="input"
                                    >
                                        <option value="none">None</option>
                                        <option value="grayscale">Grayscale</option>
                                        <option value="brightness">Increase Brightness</option>
                                        <option value="contrast">Increase Contrast</option>
                                        <option value="sharpen">Sharpen</option>
                                    </select>
                                </div>

                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontSize: 'var(--text-sm)',
                                        fontWeight: 'var(--font-medium)',
                                        marginBottom: 'var(--spacing-2)',
                                        color: 'var(--color-text-secondary)'
                                    }}>
                                        OCR Language
                                    </label>
                                    <select
                                        value={ocrLanguage}
                                        onChange={(e) => setOCRLanguage(e.target.value)}
                                        className="input"
                                        disabled={!applyOCR}
                                    >
                                        <option value="eng">English</option>
                                        <option value="spa">Spanish</option>
                                        <option value="fra">French</option>
                                        <option value="deu">German</option>
                                        <option value="chi_sim">Chinese (Simplified)</option>
                                        <option value="ara">Arabic</option>
                                    </select>
                                </div>
                            </div>

                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--spacing-3)',
                                padding: 'var(--spacing-4)',
                                backgroundColor: 'var(--color-surface-elevated)',
                                borderRadius: 'var(--radius-md)'
                            }}>
                                <input
                                    type="checkbox"
                                    id="ocr-checkbox"
                                    checked={applyOCR}
                                    onChange={(e) => setApplyOCR(e.target.checked)}
                                    style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                                />
                                <label htmlFor="ocr-checkbox" style={{
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: 'var(--font-medium)',
                                    cursor: 'pointer',
                                    flex: 1
                                }}>
                                    <Eye size={16} style={{ display: 'inline', marginRight: 'var(--spacing-2)' }} />
                                    Enable OCR (Optical Character Recognition)
                                </label>
                                {applyOCR && <Badge variant="success">Enabled</Badge>}
                            </div>

                            {applyOCR && (
                                <Alert variant="info" style={{ marginTop: 'var(--spacing-4)' }}>
                                    <strong>OCR Enabled:</strong> Text will be extracted from images. This may take longer to process.
                                </Alert>
                            )}
                        </Card>
                    </div>

                    {/* Images List */}
                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Card>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: 'var(--spacing-4)'
                            }}>
                                <h3 style={{
                                    fontSize: 'var(--text-xl)',
                                    fontWeight: 'var(--font-bold)'
                                }}>
                                    <FileImage size={20} style={{ display: 'inline', marginRight: 'var(--spacing-2)' }} />
                                    Scanned Images ({images.length})
                                </h3>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                        images.forEach(img => {
                                            URL.revokeObjectURL(img.preview);
                                            if (img.processedPreview) URL.revokeObjectURL(img.processedPreview);
                                        });
                                        setImages([]);
                                    }}
                                >
                                    Clear All
                                </Button>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)' }}>
                                {images.map((image, index) => (
                                    <Card
                                        key={image.id}
                                        variant="outlined"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--spacing-3)',
                                            padding: 'var(--spacing-3)'
                                        }}
                                    >
                                        {/* Preview */}
                                        <img
                                            src={image.preview}
                                            alt={image.name}
                                            style={{
                                                width: '80px',
                                                height: '80px',
                                                objectFit: 'cover',
                                                borderRadius: 'var(--radius-md)',
                                                flexShrink: 0,
                                                border: '1px solid var(--color-border)'
                                            }}
                                        />

                                        {/* Info */}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{
                                                fontSize: 'var(--text-sm)',
                                                fontWeight: 'var(--font-medium)',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                marginBottom: 'var(--spacing-1)'
                                            }}>
                                                {index + 1}. {image.name}
                                            </div>
                                            <div style={{
                                                fontSize: 'var(--text-xs)',
                                                color: 'var(--color-text-tertiary)'
                                            }}>
                                                {formatFileSize(image.size)}
                                            </div>
                                        </div>

                                        {/* Actions */}
                                        <div style={{ display: 'flex', gap: 'var(--spacing-2)', flexShrink: 0 }}>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                icon
                                                onClick={() => moveImage(index, 'up')}
                                                disabled={index === 0}
                                            >
                                                <ArrowUp size={16} />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                icon
                                                onClick={() => moveImage(index, 'down')}
                                                disabled={index === images.length - 1}
                                            >
                                                <ArrowDown size={16} />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                icon
                                                onClick={() => removeImage(image.id)}
                                            >
                                                <Trash2 size={16} color="var(--color-error)" />
                                            </Button>
                                        </div>
                                    </Card>
                                ))}
                            </div>
                        </Card>
                    </div>

                    {/* Progress Bar */}
                    {isProcessing && (
                        <div style={{ marginTop: 'var(--spacing-6)' }}>
                            <ProgressBar value={progress} striped showLabel />
                            {applyOCR && (
                                <p style={{
                                    textAlign: 'center',
                                    marginTop: 'var(--spacing-2)',
                                    fontSize: 'var(--text-sm)',
                                    color: 'var(--color-text-secondary)'
                                }}>
                                    Processing OCR... This may take a while.
                                </p>
                            )}
                        </div>
                    )}

                    {/* Convert Button */}
                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Button
                            variant="primary"
                            size="lg"
                            onClick={convertToPDF}
                            disabled={isProcessing}
                            loading={isProcessing}
                            fullWidth
                        >
                            <Download size={20} />
                            {applyOCR ? 'Scan with OCR & Convert to PDF' : 'Convert to PDF'}
                        </Button>
                    </div>
                </>
            )}

            {/* PDF Preview Modal */}
            {previewBlob && (
                <PDFPreview
                    pdfBlob={previewBlob}
                    filename={previewFilename}
                    onClose={() => {
                        setPreviewBlob(null);
                        // Clean up images after preview is closed
                        images.forEach(img => {
                            URL.revokeObjectURL(img.preview);
                            if (img.processedPreview) URL.revokeObjectURL(img.processedPreview);
                        });
                        setImages([]);
                    }}
                    onDownload={() => {
                        if (previewBlob && previewFilename) {
                            saveAs(previewBlob, previewFilename);
                            toast.success('Download started!');
                            setPreviewBlob(null);
                            // Clean up images after download
                            images.forEach(img => {
                                URL.revokeObjectURL(img.preview);
                                if (img.processedPreview) URL.revokeObjectURL(img.processedPreview);
                            });
                            setImages([]);
                        }
                    }}
                />
            )}
        </div>
    );
};

export default ScanToPDF;
